import { useMemo, useState } from "react";
import { useI18n } from "@wato/i18n/react";
import { Button, Illustration, Stack } from "@wato/ui";
import { ApiErrorState } from "../../../components/ApiErrorState";
import { PageLoading } from "../../../components/common";
import { useWorkspace } from "../../workspace/WorkspaceContext";
import {
  cardText,
  inSection,
  key,
  olderSummaryIds,
  str,
  type InboxSection,
  type Tr,
} from "./console-text";
import { ProposalCard } from "./ProposalCard";
import { SummaryCard } from "./SummaryCard";
import { useAssistantInbox } from "./useAssistantInbox";

/** OWN-317: summaries / questions / proposals from the old Manager Console, inside the assistant panel. */
export function AssistantInbox({ section, onRead }: { section: InboxSection; onRead: () => void }) {
  const { t, locale } = useI18n();
  const { workspace } = useWorkspace();
  const { state, load, loadMore, loadingMore, decide, busyId, decideError } = useAssistantInbox(
    workspace.id,
    onRead,
  );
  const [olderOpen, setOlderOpen] = useState(false);
  const tr: Tr = (k, params) => t(key(k), params);
  const items = state.status === "ready" ? state.items : [];
  const older = useMemo(() => olderSummaryIds(items), [items]);
  const visible = items.filter(
    (m) => inSection(m.kind, section) && (olderOpen || !older.has(m.id)),
  );
  const timeFmt = new Intl.DateTimeFormat(locale === "ar" ? "ar-u-nu-latn" : "en", {
    timeStyle: "short",
  });

  if (state.status === "loading") return <PageLoading />;
  if (state.status === "error") {
    return <ApiErrorState error={state.error} onRetry={load} workspaceId={workspace.id} />;
  }
  if (items.every((m) => !inSection(m.kind, section))) {
    return (
      <Stack gap={2} className="items-center py-6 text-center" data-testid="assistant-inbox-empty">
        <Illustration name="inbox" size={72} />
        <p className="m-0 text-sm text-fg-muted">{t(key("empty.title"))}</p>
      </Stack>
    );
  }

  return (
    <div
      className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto"
      data-testid="assistant-inbox"
    >
      {section === "summaries" && older.size > 0 && (
        <Button
          variant="secondary"
          size="sm"
          data-testid="console-older-summaries"
          onClick={() => setOlderOpen((v) => !v)}
        >
          {tr(olderOpen ? "summary.hideOlder" : "summary.showOlder")}
        </Button>
      )}
      <ul className="m-0 flex list-none flex-col gap-3 p-0">
        {visible.map((m) => {
          const time = timeFmt.format(new Date(m.createdAt));
          const { title, body } = cardText(m, (k) => t(k));
          return (
            <li
              key={m.id}
              data-testid={`console-card-${m.kind}`}
              className="flex flex-col gap-1 rounded-lg border border-line bg-surface p-3 text-sm text-fg"
            >
              {m.kind === "proposal" ? (
                <ProposalCard
                  m={m}
                  tr={tr}
                  locale={locale}
                  busy={busyId === m.id}
                  onDecide={(verb) => decide(m, verb)}
                />
              ) : m.kind === "summary" ? (
                <SummaryCard
                  m={m}
                  tr={tr}
                  locale={locale}
                  time={time}
                  answerNowTo={`/w/${workspace.id}/ai/faq-mining`}
                  billingTo={`/w/${workspace.id}/settings/billing`}
                />
              ) : m.kind === "proposal_result" ? (
                <p>{tr(`result.${str(m.payload.code) || "failed"}`)}</p>
              ) : (
                <>
                  {title && <p className="font-semibold">{title}</p>}
                  {body && <p className="whitespace-pre-wrap break-words">{body}</p>}
                </>
              )}
            </li>
          );
        })}
      </ul>
      {decideError ? (
        <p role="alert" className="m-0 text-sm text-danger-fg">
          {tr("proposal.error")}
        </p>
      ) : null}
      {state.nextCursor && (
        <Button variant="secondary" size="sm" loading={loadingMore} onClick={loadMore}>
          {tr("loadOlder")}
        </Button>
      )}
    </div>
  );
}
